import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ConfigService} from '@app/@core/@config/config.service';
import {CampaignDraft} from '@app/@core/advertising/campaign/campaign-draft';
import {catchError, map} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {finalize, Observable, of} from 'rxjs';
import {CreativeResource} from '@app/@core/@rest/advertising/creative-resource.service';
import {Uuid} from "@shared/utils/uuid";
import {I18nFormatService} from "@app/@i18n/services/i18n-format.service";
import {Creative} from "@app/@core/@models/advertising/creative.model";
import {
  ProductData
} from '@app/campaigns/campaign/@common/campaign-drafting/product-selection/product-data-helper.service';

@Component({
  selector: 'app-campaign-drafting[campaignDraft][disabled]',
  templateUrl: './campaign-drafting.component.html',
  styleUrls: ['./campaign-drafting.component.scss'],
})
export class CampaignDraftingComponent implements OnInit, OnChanges {
  @Input() campaignDraft!: CampaignDraft
  @Input() disabled!: boolean
  @Input() productData!: ProductData
  isSubmitting: boolean = false;
  createMissingCreative$?: Observable<{ errorKey?: string }>;
  advertIndex = 0;
  MAX_CREATIVES = 5;

  constructor(
    public configService: ConfigService,
    private creativeResource: CreativeResource,
    private i18nFormatService: I18nFormatService
  ) {
  }

  ngOnInit(): void {
    let formIndex = -1;
    const invalid = this.campaignDraft.advertDrafts.find((advertDraft, index) => {
      formIndex = index;
      return advertDraft.creative.uiStatus !== 'VALID' || advertDraft.creative.userState === 'REJECTED';
    });
    this.advertIndex = !invalid ? -1 : formIndex || 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    this.handleMissingCreatives();
    if (this.advertIndex >= this.campaignDraft.advertDrafts.length) {
      this.advertIndex = 0;
    }
    this.campaignDraft.advertDrafts.forEach((advertDraft) => {
      advertDraft.creative.uiStatus = advertDraft.creative.isNew ? 'NEW' : this.checkCreative(advertDraft.creative) && advertDraft.advertForm.valid ? 'VALID' : 'INVALID';
    });
  }

  copyClick(clickedIndex: number): void {
    const refCreative = structuredClone(this.campaignDraft.advertDrafts[clickedIndex].creative);
    refCreative.id = Uuid.generate();
    refCreative.isNew = true;
    refCreative.uiStatus = 'NEW';
    refCreative.name = refCreative.name + this.i18nFormatService.translate('campaign.ad.copyTitle');
    this.campaignDraft.advertDrafts[clickedIndex].addAnotherAdvertWithCreative(refCreative);
    this.advertIndex = this.campaignDraft.advertDrafts.length;
  }

  showLoadingOverlays(): boolean {
    return !!this.createMissingCreative$ || this.isSubmitting || this.disabled;
  }

  viewClick(clickedIndex: number): void {
    if (this.advertIndex === clickedIndex) {
      this.advertIndex = -1;
    } else {
      this.advertIndex = clickedIndex;
    }
  }

  addMade(): void {
    this.advertIndex = this.campaignDraft.advertDrafts.length - 1;
  }

  submitValidationWarnings() {
    return this.campaignDraft.lineItemReview.lineItem.validationWarnings.filter(
      (message) => {
        return message.code !== 'validation.warning.lineItem.unapproved'
      }
    )
  }

  private checkCreative(creative: Creative): boolean {
    const relevantCreative = this.campaignDraft.lineItemReview.creatives.find(c => {
      return c.creative.id === creative.id;
    });
    return !relevantCreative ? false : !this.isInvalid(relevantCreative.creative);
  }

  showSubmitCampaignComponent(): boolean {
    const warnings = this.submitValidationWarnings();
    if (warnings.length === 0 && this.campaignDraft.canSubmit()) {
      return true;
    }
    if (warnings.length === 1 && warnings[0].code === 'validation.warning.lineItem.no.valid.creatives') {
      const invalidCreatives = this.campaignDraft.lineItemReview.creatives.filter(c => {
        return this.isInvalid(c.creative);
      });
      return invalidCreatives.length === 0;
    }
    return false;
  }

  private isInvalid(creative: Creative) {
    return creative.validationWarnings && creative.validationWarnings.length > 0 && !(creative.validationWarnings.length === 1 && creative.validationWarnings[0].code === 'validation.warning.creative.rejected');
  }

  handleMissingCreatives() {
    if (this.campaignDraft.lineItemReview.creatives.length > 0) {
      return;
    }
    const productId = this.campaignDraft.lineItemReview.lineItem.product!.id;
    const product = this.campaignDraft.products.find((p) => p.id === productId);
    if (!product) {
      return;
    }
    this.createMissingCreative$ = this.creativeResource.update(
      this.campaignDraft.campaignDraftFactory.newCreative(
        product,
        this.campaignDraft.lineItemReview.lineItem.id,
        1)
    ).pipe(
        // success, reload lineItemReview from server
        map(() => {
          this.campaignDraft.sendUpdate({reloadLineItemReview: true});
          return ({});
        }),
        // error handling
        catchError((error: HttpErrorResponse) => {
          return of({errorKey: error.error?.code || error.statusText || error});
        }),
        // clean up after sequence is done
        finalize(() => this.createMissingCreative$ = undefined)
      );
  }
}
